import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "contexts/AuthContext";

import PageHeader from "components/PageHeader";
import useActivityReportData from "layouts/tables/data/activityReportData";
import { resolveDateRangePreset } from "utils/dateRangePresets";

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

function ModuleCard({
  icon,
  title,
  total,
  totalLabel,
  tone,
  breakdown,
  breakdownLabel,
  extra,
  onClick,
}) {
  const theme = useTheme();
  const toneMap = {
    primary: theme.palette.teal?.[600] || theme.palette.primary.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    purple: "#6D28D9",
  };
  const color = toneMap[tone] || toneMap.primary;

  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2.5,
        height: "100%",
        ...(onClick && {
          cursor: "pointer",
          transition: "transform 0.15s, box-shadow 0.15s",
          "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            bgcolor: `${color}1A`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small">{icon}</Icon>
        </Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      </Stack>

      <Typography variant="h3" fontWeight={800} sx={{ color }}>
        {total}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {totalLabel}
      </Typography>

      {breakdown && breakdown.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {breakdownLabel && (
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              {breakdownLabel}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {breakdown.map(([label, count]) => (
              <Chip key={label} size="small" label={`${titleCase(label)}: ${count}`} />
            ))}
          </Stack>
        </Box>
      )}

      {extra}
    </Card>
  );
}

ModuleCard.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  totalLabel: PropTypes.string,
  tone: PropTypes.string,
  breakdown: PropTypes.array,
  breakdownLabel: PropTypes.string,
  extra: PropTypes.node,
  onClick: PropTypes.func,
};

function SectionTitle({ children }) {
  return (
    <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
      {children}
    </Typography>
  );
}
SectionTitle.propTypes = { children: PropTypes.node };

function ActivityReportCenter() {
  const { church } = useAuth();
  const navigate = useNavigate();
  const {
    report,
    isLoading,
    error,
    from,
    setFrom,
    to,
    setTo,
    handleApplyFilter,
    handleClearFilter,
    applyDateRange,
  } = useActivityReportData();

  const [dateRangeType, setDateRangeType] = useState("custom");

  const handleDateRangeTypeChange = (e) => {
    const type = e.target.value;
    setDateRangeType(type);
    const range = resolveDateRangePreset(type);
    if (range) applyDateRange(range.from, range.to);
  };

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading activity report...</Typography>;
  }
  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  const d = report || {};
  const periodLabel =
    from || to ? `${from || "Inception"} to ${to || "Date"}` : "All recorded activity";

  return (
    <Box>
      <PageHeader
        title="Activity Report Center"
        subtitle="A church-wide view of growth, engagement and pastoral activity."
        actions={
          <>
            <TextField
              select
              size="small"
              value={dateRangeType}
              onChange={handleDateRangeTypeChange}
              sx={{ width: 120 }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </TextField>

            {dateRangeType === "custom" && (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button variant="contained" onClick={handleApplyFilter}>
                  Update
                </Button>
              </>
            )}
            <Button variant="outlined" onClick={handleClearFilter}>
              Clear
            </Button>
            <IconButton
              onClick={() => window.print()}
              sx={{ border: 1, borderColor: "divider" }}
              title="Print report"
            >
              <Icon fontSize="small">print</Icon>
            </IconButton>
          </>
        }
      />

      <Card sx={{ p: 3, mb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {church?.name || "Church"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Activity &amp; Engagement Report
            </Typography>
          </Box>
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Period: {periodLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Generated: {new Date().toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Card>

      <SectionTitle>Growth &amp; Outreach</SectionTitle>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="emoji_people"
            title="New Converts"
            total={d.new_converts?.total ?? 0}
            totalLabel="recorded in period"
            tone="success"
            breakdown={Object.entries(d.new_converts?.by_status || {})}
            breakdownLabel="By status"
            onClick={() => navigate("/new-converts")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="how_to_reg"
            title="Visitors"
            total={d.visitors?.total ?? 0}
            totalLabel="logged in period"
            tone="info"
            breakdown={Object.entries(d.visitors?.by_status || {})}
            breakdownLabel="By status"
            onClick={() => navigate("/visitors")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="diversity_3"
            title="Small Groups"
            total={d.small_groups?.total ?? 0}
            totalLabel={`${d.small_groups?.active ?? 0} active · ${
              d.small_groups?.total_members ?? 0
            } members`}
            tone="purple"
            onClick={() => navigate("/small-groups")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="groups_2"
            title="Serving Teams"
            total={d.serving_teams?.total ?? 0}
            totalLabel={`${d.serving_teams?.active ?? 0} active · ${
              d.serving_teams?.total_members ?? 0
            } members`}
            tone="warning"
            onClick={() => navigate("/serving-teams")}
          />
        </Grid>
      </Grid>

      <SectionTitle>Engagement</SectionTitle>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="how_to_reg"
            title="Attendance"
            total={d.attendance?.total ?? 0}
            totalLabel="check-ins in period"
            tone="primary"
            breakdown={Object.entries(d.attendance?.by_method || {})}
            breakdownLabel="By method"
            onClick={() => navigate("/reports/attendance")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="event"
            title="Events"
            total={d.events?.total ?? 0}
            totalLabel={`${d.events?.upcoming ?? 0} upcoming · ${d.events?.past ?? 0} past`}
            tone="info"
            onClick={() => navigate("/calendar")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="child_care"
            title="Child Check-in"
            total={d.child_checkin?.total_checkins ?? 0}
            totalLabel={`${d.child_checkin?.currently_in ?? 0} currently checked in`}
            tone="success"
            onClick={() => navigate("/child-checkin")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="apartment"
            title="Departments"
            total={d.departments?.total ?? 0}
            totalLabel={`${d.departments?.total_members ?? 0} members assigned`}
            tone="purple"
            onClick={() => navigate("/departments")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModuleCard
            icon="volunteer_activism"
            title="Ministries"
            total={d.ministries?.total ?? 0}
            totalLabel={`${d.ministries?.total_members ?? 0} members assigned`}
            tone="warning"
            onClick={() => navigate("/ministries")}
          />
        </Grid>
      </Grid>

      <SectionTitle>Pastoral Care</SectionTitle>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={4}>
          <ModuleCard
            icon="volunteer_activism"
            title="Prayer Requests"
            total={d.pastoral_care?.prayer_requests_total ?? 0}
            totalLabel="logged in period"
            tone="info"
            breakdown={Object.entries(d.pastoral_care?.prayer_requests_by_status || {})}
            breakdownLabel="By status"
            onClick={() => navigate("/pastoral-care")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ModuleCard
            icon="home"
            title="Pastoral Visits"
            total={d.pastoral_care?.visits_total ?? 0}
            totalLabel="logged in period"
            tone="success"
            breakdown={Object.entries(d.pastoral_care?.visits_by_type || {})}
            breakdownLabel="By type"
            onClick={() => navigate("/pastoral-care")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ModuleCard
            icon="church"
            title="Sacramental Records"
            total={d.sacramental_records?.total ?? 0}
            totalLabel="recorded in period"
            tone="purple"
            breakdown={Object.entries(d.sacramental_records?.by_type || {})}
            breakdownLabel="By type"
            onClick={() => navigate("/sacramental-records")}
          />
        </Grid>
      </Grid>

      <SectionTitle>Communication</SectionTitle>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <ModuleCard
            icon="sms"
            title="Birthday/Anniversary SMS"
            total={d.sms?.birthday_anniversary_sent ?? 0}
            totalLabel="sent in period"
            onClick={() => navigate("/sms")}
            tone="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ModuleCard
            icon="mail"
            title="Bulk Email"
            total={d.email?.total ?? 0}
            totalLabel={`${d.email?.sent ?? 0} sent · ${d.email?.failed ?? 0} failed`}
            tone="info"
            onClick={() => navigate("/bulk-email")}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default ActivityReportCenter;
